import { yupResolver } from "@hookform/resolvers/yup";
import { Control, useForm } from "react-hook-form";
import { useEffect, useMemo, useState } from "react";
import useStyles from "./styles";
import { Autocomplete, Button, IconTitle, Input, Upload } from '../../../../../../components';
import Shop, { ShopType } from "../../../../../../entities/shop.entity";
import { schema, ShopForm } from "../../../../../../validations/shop.validation";
import { checkEmptyObject, removeUndefinedValues } from "../../../../../../utils/objects";
import states from "../../../../../Condominium/states";
import ConfigurationIcon from "../../../../../../components/ConfigurationIcon";
import ShopGroup from "../../../../../../entities/shop-group.entity";
import { clone } from "../../../../../../utils/helpers";

interface FormProps {
  shop: Shop;
  shopGroups?: ShopGroup[];
  fileToUpload?: File | null;
  onSubmit: (input: Shop) => unknown;
  handleUpload: (file: File | null) => void;
}

export const Form = ({ shop, shopGroups, onSubmit, fileToUpload, handleUpload }: FormProps): JSX.Element => {
  const styles = useStyles();
  const { register, reset, getValues, handleSubmit: handleValidation, watch, formState: { errors, isSubmitting }, trigger, setValue, control } = useForm<ShopForm>({ resolver: yupResolver(schema) });
  const [shopType, setShopType] = useState<ShopType>(ShopType.Prestashop);

  const onClickSubmit = async () => {
    const values = getValues();

    return onSubmit({
      ...shop,
      type: shopType,
      ...removeUndefinedValues(values, { shouldRemoveBlankStrings: false }),
      address: checkEmptyObject(values.address)
    });
  };

  const currentShopGroup = watch('groupId');
  const currentState = watch('address.stateCode');

  useEffect(() => {
    reset(clone(shop));
  }, [shop, reset]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.left}>
          <Upload onUpload={handleUpload} file={fileToUpload || shop.imageUrl} />
        </div>

        <div className={styles.right}>
          <div className={styles.line}>
            <Input
              label="nome da loja"
              validationRegister={register('label')}
              placeholder="Loja do Apepê"
              fullWidth
            />
            <Input
              label="e-mail de contato"
              validationRegister={register('contactEmail')}
              placeholder="exemplo@mail.com.br"
              fullWidth
              helperText={errors.contactEmail?.message || ''}
            />
          </div>
          <div className={styles.line}>
            <Autocomplete
              label="grupo de loja"
              value={shopGroups?.find(({ id }) => id === currentShopGroup) || null}
              placeholder="Selecione um grupo de loja"
              options={shopGroups ?? []}
              fullWidth
              onChange={(_, value) => {
                setValue('groupId', value?.id!);
              }}
            />
          </div>
          <div className={styles.lineAddress}>
            <Input
              label="endereço"
              placeholder="Ex: Alameda dos Anjos"
              validationRegister={register('address.streetName')}
              fullWidth
              error={Boolean(errors.address?.streetName)}
              helperText={errors.address?.streetName?.message || ' '}
            />
            <Input
              label="bairro"
              placeholder="Ex: Jardins"
              validationRegister={register('address.neighborhood')}
              fullWidth
              error={Boolean(errors.address?.neighborhood)}
              helperText={errors.address?.neighborhood?.message || ' '}
            />
            <Input
              label="numero"
              placeholder="Ex: 123"
              validationRegister={register('address.buildingNumber')}
              sx={{ width: '30%' }}
              error={Boolean(errors.address?.buildingNumber)}
              helperText={errors.address?.buildingNumber?.message || ' '}
            />
            <Input
              label="cep"
              placeholder="Ex: 00000-000"
              mask={{ mask: '00000-000' }}
              autoComplete="off"
              sx={{ width: '50%' }}
              validationRegister={register('address.postalCode')}
              control={control as unknown as Control}
              error={Boolean(errors.address?.postalCode)}
              helperText={errors.address?.postalCode?.message || ' '}
            />
          </div>
          <div className={styles.line}>
            <Input
              label="cidade"
              placeholder="Ex: São Paulo"
              validationRegister={register('address.city')}
              fullWidth
              error={Boolean(errors.address?.city)}
              helperText={errors.address?.city?.message || ' '}
            />
            <Autocomplete
              label="estado"
              placeholder="Ex: SP"
              options={states}
              getOptionLabel={(option) => option.code}
              sx={{ width: '30%' }}
              value={states.find(({ code }) => code === currentState) || null}
              onChange={(_, data) => {
                setValue('address.stateCode', data?.code);
                trigger('address.stateCode');
              }}
              error={Boolean(errors.address?.stateCode)}
              helperText={errors.address?.stateCode?.message || ' '}
            />
          </div>
          <div className={styles.line}>
            <Autocomplete
              fullWidth
              defaultValue={shopType}
              options={[ShopType.External, ShopType.Prestashop]}
              label="tipo de loja"
              onChange={(_, value) => {
                setShopType(value || ShopType.Prestashop);
              }}
            />
          </div>
          {shopType === 'prestashop' && (
            <>
              <div className={styles.section}>
                <ConfigurationIcon title="Prestashop" />
              </div>
              <div className={styles.line}>
                <Input label="url da loja" validationRegister={register('prestashopBaseUrl')} placeholder="Ex: 1" fullWidth />
                <Input label="token" validationRegister={register('prestashopAuthToken')} placeholder="Ex: 1" fullWidth />
                <Input
                  label="id da loja"
                  placeholder="Ex: 1"
                  fullWidth
                  validationRegister={register('prestashopShopId')}
                />
              </div>
              <div className={styles.line}>
                <Input label="id de endereço" placeholder="Ex: 1" validationRegister={register('prestashopAddressId')} fullWidth />
                <Input label="id da transportadora" placeholder="Ex: 1" validationRegister={register('prestashopCarrierId')} fullWidth />
                <Input label="id da moeda" placeholder="Ex: 1" validationRegister={register('prestashopCurrencyId')} fullWidth />
                <Input label="id grupo da loja" placeholder="Ex: 1" validationRegister={register('prestashopGroupShopId')} fullWidth />
                <Input label="id do módulo" placeholder="Ex: 1" validationRegister={register('prestashopModuleId')} fullWidth />
                <Input label="id da linguagem" placeholder="Ex: 1" validationRegister={register('prestashopLangId')} fullWidth />
                <Input label="id da categoria pai" placeholder="Ex: 1" validationRegister={register('prestashopCategoryParentId')} fullWidth />
                <Input label="id do grupo de usuarios" placeholder="Ex: 1" validationRegister={register('prestashopCustomerGroupId')} fullWidth />
              </div>
              <div className={styles.line}>
                <Input
                  label="id status de falha de pagamento"
                  placeholder="Ex: 1"
                  validationRegister={register('prestashopPaymentFailedStateId')}
                  fullWidth
                />
                <Input label="id status de pagamento" placeholder="Ex: 1" validationRegister={register('prestashopPaidStateId')} fullWidth />
                <Input
                  label="id status de aguardando pagamento"
                  placeholder="Ex: 1"
                  validationRegister={register('prestashopWaitingPaymentStateId')}
                  value={shop.prestashopWaitingPaymentStateId}
                  fullWidth
                />
                <Input label="id status de cancelamento" placeholder="Ex: 1" validationRegister={register('prestashopCanceledStateId')} fullWidth />
              </div>
            </>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <Button
          sx={{ width: '200px' }}
          onClick={handleValidation(onClickSubmit)}
          loading={isSubmitting}
        >
          Salvar
        </Button>
      </div>
    </div>
  );
};

export default Form;
